/**
 * 이메일 대량 발송 API
 * @description CSV 파일 기반 대량 멤버 초대 및 임시패스워드 발송
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
import instance from "./instance";
import { ENV } from "@/constants/env";

// ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
const PREFIX_PATH = `${ENV.PRE_FIX}`;

/**
 * 대량 임시패스워드 발송 요청 DTO
 */
export interface BulkTempPasswordRequest {
  emails: string[];
  companyId: number;
  companyName?: string;
}

/**
 * 대량 이메일 발송 결과 DTO
 */
export interface BulkEmailResult {
  total: number;
  successful: number;
  failed: number;
  errors?: Array<{
    email: string;
    error: string;
  }>;
}

export interface EmailLog {
  logId: number;
  email: string;
  recipientName: string;
  sendStatus: "SUCCESS" | "FAILED" | "PENDING";
  sendType: string;
  subject: string;
  emailService: string;
  errorMessage?: string;
  sentAt?: string;
  regDttm: string;
  companyId: number;
  memberId?: number;
}

export interface EmailLogResponse {
  success: boolean;
  message: string;
  data: {
    logs: EmailLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    stats: {
      total: number;
      successful: number;
      failed: number;
      pending: number;
      successRate: string;
    };
  };
}

// axios 인터셉터 응답 타입
interface ApiResponseWrapper<T> {
  success: boolean;
  message: string;
  data: T;
}

// axios 에러 타입
interface AxiosError {
  message: string;
  response?: {
    status: number;
    data?: { message?: string };
    headers?: Record<string, string>;
  };
  config?: Record<string, unknown>;
  code?: string;
  stack?: string;
}

/**
 * 대량 이메일 API 클라이언트
 * @description 이메일 대량 발송 관련 모든 API 호출을 담당합니다.
 */
export const bulkEmailApi = {
  /**
   * 대량 임시패스워드 발송
   * @param data 발송 요청 데이터
   * @returns 발송 결과
   */
  async sendTempPasswords(
    data: BulkTempPasswordRequest
  ): Promise<BulkEmailResult> {
    // ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
    const apiUrl = `${PREFIX_PATH}/bulk-email/temp-password/send`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "POST",
      data,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    try {
      const response = await instance.post<{
        success: boolean;
        message: string;
        data: BulkEmailResult;
      }>(apiUrl, data);

      console.log("[API_RESPONSE_RAW]", {
        url: apiUrl,
        fullResponse: response,
      });

      // axios 인터셉터가 response.data를 반환하므로 response가 실제 데이터
      const responseData =
        response as unknown as ApiResponseWrapper<BulkEmailResult>;

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: responseData.success,
        message: responseData.message,
        result: responseData.data,
      });

      // ✅ 백엔드 ResponseDto.success 필드 검증 필수
      if (!responseData.success) {
        throw new Error(responseData.message || "API request failed");
      }

      // ✅ 데이터 존재 여부 검증
      if (!responseData.data) {
        throw new Error("No data returned from server");
      }

      return responseData.data;
    } catch (error: unknown) {
      const err = error as unknown as AxiosError;
      console.error("[API_ERROR_DETAILED]", {
        api: "sendTempPasswords",
        data,
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers,
        config: err.config,
        code: err.code,
        stack: err.stack,
      });

      // ✅ 사용자 친화적 에러 메시지 생성
      let userMessage = "An error occurred. Please try again.";

      if (err.response?.status === 400) {
        userMessage = "Please check the company ID and email data.";
      } else if (err.response?.status === 401) {
        userMessage = "Authentication failed. Please log in again.";
      } else if (err.response?.status === 500) {
        userMessage = "Server error occurred. Please try again later.";
      } else if (err.response?.data?.message) {
        userMessage = err.response.data.message;
      }

      throw new Error(userMessage);
    }
  },

  /**
   * 대량 멤버 생성 (CSV 기반)
   * @description 향후 확장을 위한 API (현재는 sendTempPasswords 사용)
   * @param data 멤버 생성 요청 데이터
   * @returns 생성 결과
   */
  async createBulkMembers(data: {
    companyId: number;
    members: Array<{
      email: string;
      memberNm: string;
      firstName: string;
      lastName?: string;
    }>;
  }): Promise<BulkEmailResult> {
    // 향후 구현 예정
    // const apiUrl = `${PREFIX_PATH}/admin/members/bulk-invite`;
    console.log("[API_PLACEHOLDER]", {
      api: "createBulkMembers",
      data,
      note: "향후 구현 예정 - 현재는 sendTempPasswords 사용",
    });

    // 임시로 sendTempPasswords 호출
    return this.sendTempPasswords({
      emails: data.members.map((m) => m.email),
      companyId: data.companyId,
      companyName: "WORLD-TKDLAB",
    });
  },

  /**
   * 이메일 발송 로그 조회
   * @param params 조회 파라미터
   * @returns 이메일 발송 로그
   */
  async getEmailLogs(
    params: {
      companyId?: number;
      page?: number;
      limit?: number;
      status?: "SUCCESS" | "FAILED" | "PENDING";
    } = {}
  ): Promise<EmailLogResponse["data"]> {
    const apiUrl = `${PREFIX_PATH}/bulk-email/logs`;

    const queryParams = new URLSearchParams();
    if (params.companyId)
      queryParams.append("companyId", params.companyId.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.status) queryParams.append("status", params.status);

    const fullUrl = queryParams.toString()
      ? `${apiUrl}?${queryParams.toString()}`
      : apiUrl;

    console.log("[API_CALL]", {
      url: fullUrl,
      method: "GET",
      params,
    });

    try {
      const response = await instance.get<EmailLogResponse>(fullUrl);

      // axios 인터셉터가 response.data를 반환하므로 response가 실제 데이터
      const responseData = response as unknown as EmailLogResponse;

      console.log("[API_RESPONSE]", {
        url: fullUrl,
        success: responseData.success,
        logsCount: responseData.data?.logs?.length || 0,
        stats: responseData.data?.stats,
      });

      if (!responseData.success) {
        throw new Error(responseData.message || "Failed to fetch email logs");
      }

      return responseData.data;
    } catch (error: unknown) {
      const err = error as unknown as AxiosError;
      console.error("[API_ERROR]", {
        api: "getEmailLogs",
        params,
        error: err.message,
        response: err.response?.data,
      });

      throw new Error(
        err.response?.data?.message || "Failed to fetch email logs"
      );
    }
  },

  /**
   * 특정 회사의 최근 이메일 발송 로그 조회
   * @param companyId 회사 ID
   * @param limit 조회할 로그 수
   * @returns 최근 이메일 발송 로그
   */
  async getRecentEmailLogs(
    companyId: number,
    limit: number = 20
  ): Promise<{
    companyId: number;
    logs: EmailLog[];
    total: number;
  }> {
    const apiUrl = `${PREFIX_PATH}/bulk-email/logs/company/${companyId}/recent?limit=${limit}`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      companyId,
      limit,
    });

    try {
      const response = await instance.get<{
        success: boolean;
        message: string;
        data: {
          companyId: number;
          logs: EmailLog[];
          total: number;
        };
      }>(apiUrl);

      // axios 인터셉터가 response.data를 반환하므로 response가 실제 데이터
      const responseData = response as unknown as ApiResponseWrapper<{
        companyId: number;
        logs: EmailLog[];
        total: number;
      }>;

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: responseData.success,
        logsCount: responseData.data?.logs?.length || 0,
      });

      if (!responseData.success) {
        throw new Error(
          responseData.message || "Failed to fetch recent email logs"
        );
      }

      return responseData.data;
    } catch (error: unknown) {
      const err = error as unknown as AxiosError;
      console.error("[API_ERROR]", {
        api: "getRecentEmailLogs",
        companyId,
        limit,
        error: err.message,
        response: err.response?.data,
      });

      throw new Error(
        err.response?.data?.message || "Failed to fetch recent email logs"
      );
    }
  },
};
